/**
 * 메시지 핸들러 순수 함수들
 * 스토어에 직접 접근하지 않고, 처리 결과만 반환
 */

import type {
  ChunkMessage,
  TextChunkMessage,
  SttResultMessage,
  ErrorMessage,
  ConnectedMessage,
  InterruptedMessage,
  AvatarStatusMessage,
} from "../types/websocket-messages";
import type { ServiceStatusMessage } from "../types/service-status-messages";

export interface MessageHandlerContext {
  currentTurnId: string | null;
  sessionId: string | null;
}

export interface HandlerResult<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * 연결 메시지 핸들러
 */
export function handleConnectedMessage(
  message: ConnectedMessage,
  context: MessageHandlerContext
): HandlerResult<{ avatarStatus?: AvatarStatusMessage["avatar"]; isAdmin?: boolean }> {
  console.log("🔌 WS: 서버 연결 확인", message);

  return {
    success: true,
    data: {
      avatarStatus: message.avatar_status?.avatar,
      isAdmin: message.is_admin,
    },
  };
}

/**
 * 텍스트 청크 핸들러
 */
export function handleTextChunk(
  chunk: TextChunkMessage,
  context: MessageHandlerContext
): HandlerResult<TextChunkMessage> {
  // 최신 턴만 처리한다. 이전 턴의 늦은 텍스트가 새 응답에 섞이면 안 된다.
  if (!context.currentTurnId || chunk.turn_id !== context.currentTurnId) {
    return {
      success: false,
      error: "턴 ID가 일치하지 않습니다",
    };
  }

  console.log(`📝 텍스트 청크 수신: ${chunk.sequence_num}`, chunk.text);

  return {
    success: true,
    data: chunk,
  };
}

/**
 * 음성 청크 핸들러
 */
export function handleChunk(
  chunk: ChunkMessage,
  context: MessageHandlerContext
): HandlerResult<ChunkMessage> {
  // 턴 ID 검증
  if (!context.currentTurnId || chunk.turn_id !== context.currentTurnId) {
    console.warn(
      `🚫 turnId 불일치로 chunk 무시: current=${context.currentTurnId}, received=${chunk.turn_id}`
    );
    return {
      success: false,
      error: "턴 ID가 일치하지 않습니다",
    };
  }

  console.log(`🎵 음성 청크 수신: ${chunk.sequence_num}`, chunk.text);

  return {
    success: true,
    data: chunk,
  };
}

/**
 * STT 결과 핸들러
 */
export function handleSttResult(
  sttResult: SttResultMessage,
  context: MessageHandlerContext
): HandlerResult<SttResultMessage> {
  // 턴 ID 검증
  if (!context.currentTurnId || sttResult.turn_id !== context.currentTurnId) {
    console.warn(
      `🚫 turnId 불일치로 sttResult 무시: current=${context.currentTurnId}, received=${sttResult.turn_id}`
    );
    return {
      success: false,
      error: "턴 ID가 일치하지 않습니다",
    };
  }

  console.log("🎤 STT 결과:", sttResult.text);

  return {
    success: true,
    data: sttResult,
  };
}

/**
 * 에러 메시지 핸들러
 */
export function handleError(
  error: ErrorMessage,
  context: MessageHandlerContext
): HandlerResult<ErrorMessage> {
  console.error("❌ WS: 오류", error.message);

  if (error.turn_id && error.turn_id !== context.currentTurnId) {
    return {
      success: false,
      error: "턴 ID가 일치하지 않습니다",
    };
  }

  return {
    success: true,
    data: error,
  };
}

/**
 * 중단 메시지 핸들러
 */
export function handleInterrupted(
  interrupted: InterruptedMessage,
  context: MessageHandlerContext
): HandlerResult<InterruptedMessage> {
  console.log("⏸️ WS: 중단됨", interrupted);

  if (!context.currentTurnId || interrupted.turn_id !== context.currentTurnId) {
    return {
      success: false,
      error: "턴 ID가 일치하지 않습니다",
    };
  }

  return {
    success: true,
    data: interrupted,
  };
}

/**
 * 아바타 상태 핸들러
 */
export function handleAvatarStatus(
  status: AvatarStatusMessage,
  context: MessageHandlerContext
): HandlerResult<AvatarStatusMessage> {
  console.log("📷 WS: 아바타 상태 응답 수신", status);

  return {
    success: true,
    data: status,
  };
}

/**
 * 서비스 상태 메시지 핸들러
 */
export function handleServiceStatus(
  message: ServiceStatusMessage,
  context: MessageHandlerContext
): HandlerResult<ServiceStatusMessage> {
  const logPrefix = {
    server_maintenance: "🔧",
    service_unavailable: "⏰",
    service_status: "⏳",
    admin_notice: "📢",
  }[message.type] || "ℹ️";

  console.log(`${logPrefix} WS: 서비스 상태 메시지`, message);

  return {
    success: true,
    data: message,
  };
}
